import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Dialog, TextField } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";

import {
  listTickets,
  createStripeTicketCheckoutSession,
  getUserEmail,
  getUnconfirmedPayPayPayment,
  claimFreeTicket,
} from "utils/api";
import ButtonDialog from "default/components/ButtonDialog";
import Loading from "default/components/Loading";

import { COLORS } from "constants/colors";
import AppLayout from "default/components/AppLayout";
import Error from "default/components/Error";
import InputEmail from "default/components/InputEmail";
import PayPayButton from "default/components/PayPayButton";
import Footer from "default/components/Footer";
import StyledButton from "default/components/StyledButton";
import TicketBox from "default/components/TicketBox";
import { lineAccountLinks } from "constants/links";

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripeApiKey = process.env.REACT_APP_STRIPE_API_KEY;
const stripePromise = loadStripe(stripeApiKey);

const oneDayFreeTicketKey = "1_day_free_jafes2025";

function Tickets() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const lng = queryParams.get("lng") || "ja"; // デフォルトは日本語
  const work = location.pathname.split("/")[1];

  const { t } = useTranslation(["jashindp", "common"]);

  const fontFamily = lng.startsWith("ja")
    ? '"Noto Serif JP", serif'
    : lng.startsWith("zh")
    ? '"LXGW WenKai TC", serif'
    : lng.startsWith("ko")
    ? '"Gowun Dodum", serif'
    : '"Cormorant Infant", serif';

  // クエリパラメータを取得
  const soulId = queryParams.get("soul_id");
  const character = soulId.split("_")[0];

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [openDialog, setOpenDialog] = useState(null);
  const [openCheckPayment, setOpenCheckPayment] = useState(false);
  const [merchantPaymentId, setMerchantPaymentId] = useState(null);
  const [openAnnouncement, setOpenAnnouncement] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [isError, setIsError] = useState(false);
  const [emailRequired, setEmailRequired] = useState(false);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState(null);
  const [selectedTicketKey, setSelectedTicketKey] = useState(null);
  const [purchaseMethod, setPurchaseMethod] = useState(null);
  const [claimingFree, setClaimingFree] = useState(false);
  const [freeClaimed, setFreeClaimed] = useState(false);
  const [freeClaimError, setFreeClaimError] = useState(null);
  const [openFreeTrial, setOpenFreeTrial] = useState(false);
  const [freeTrialCode, setFreeTrialCode] = useState("");
  const [freeTrialMsg, setFreeTrialMsg] = useState("");

  // Add new states for the jafes free ticket
  const [openFreeJafes, setOpenFreeJafes] = useState(false);
  const [freeJafesCode, setFreeJafesCode] = useState("");
  const [openCodeError, setOpenCodeError] = useState(false);
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);

  const getSoulName = (soulId) => {
    return soulId.split("_")[0];
  };

  const handleAnnouncementClose = () => {
    setOpenAnnouncement(false);
  };

  const handleCheckPaymentClose = () => {
    setOpenCheckPayment(false);
  };

  const purchaseWithPayPay = async () => {
    try {
      navigate(
        `/${work}/confirm-paypay?soul_id=${soulId}&episode_id=${selectedEpisodeId}&ticket_key=${selectedTicketKey}`
      );
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const purchaseWithStripe = async () => {
    try {
      const { session_id } = await createStripeTicketCheckoutSession(
        soulId,
        selectedEpisodeId,
        selectedTicketKey
      );
      const stripe = await stripePromise;
      // Stripe Checkoutページを開く
      const { error } = await stripe.redirectToCheckout({
        sessionId: session_id,
      });
      if (error) {
        console.error("Error:", error);
      }
    } catch (error) {
      console.error("Error:", error);
    }
    setIsProcessing(false);
  };

  const navigateToPurchase = async () => {
    if (purchaseMethod === "stripe") {
      purchaseWithStripe();
    } else if (purchaseMethod === "paypay") {
      purchaseWithPayPay();
    }
  };

  const onClickPurchaseMethod = async (purchaseMethod) => {
    setIsProcessing(true);
    try {
      const email = await getUserEmail();

      if (!email.email_address) {
        window.scrollTo(0, 0);
        setPurchaseMethod(purchaseMethod);
        setOpenDialog(false);
        setEmailRequired(true);
        setIsProcessing(false);
        return;
      }
    } catch (error) {
      console.error("Error:", error);
      setIsProcessing(false);
      return;
    }

    if (purchaseMethod === "stripe") {
      purchaseWithStripe();
    } else if (purchaseMethod === "paypay") {
      purchaseWithPayPay();
    }
  };

  const onClickPurchase = async (episodeId, ticketKey) => {
    setSelectedEpisodeId(episodeId);
    setSelectedTicketKey(ticketKey);

    if (ticketKey === oneDayFreeTicketKey) {
      setOpenFreeJafes(true);
      return;
    }
    setOpenDialog(true);
  };

  // Add handler for jafes code submission
  const handleFreeJafesSubmit = async () => {
    if (!freeJafesCode.trim() || isSubmittingCode) {
      return;
    }

    setIsSubmittingCode(true);

    try {
      const response = await claimFreeTicket(soulId, freeJafesCode.trim());
      setOpenFreeJafes(false);
      // Navigate to how-to-use page on success
      navigate(`/${work}/how-to-use?soul_id=${soulId}&lng=${lng}`);
    } catch (error) {
      console.error("Error:", error);
      setOpenFreeJafes(false);
      setOpenCodeError(true);
    } finally {
      setIsSubmittingCode(false);
    }
  };

  const initTickets = async (soulId) => {
    try {
      const { merchant_payment_id: merchantPaymentId } =
        await getUnconfirmedPayPayPayment(soulId);
      if (merchantPaymentId) {
        setOpenCheckPayment(true);
        setMerchantPaymentId(merchantPaymentId);
      }
    } catch (error) {
      console.error("Error:", error);
    }

    try {
      const tickets = await listTickets(soulId);
      setTickets(tickets);
    } catch (error) {
      // 修正箇所: エピソードが購入されていない場合の処理を追加
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const listOtherAccountLinks = (character) => {
    // characterと異なるもののみ返す配列
    return Object.entries(lineAccountLinks[work][lng] || {})
      .filter(([key]) => key !== character)
      .map(([name, url]) => ({ name, url }));
  };

  // 初回ロード時
  useEffect(() => {
    initTickets(soulId);
  }, []);

  // Move handlePurchaseMethod to the top (before any early returns or conditional logic)
  // This fixes the "React Hook called conditionally" error.
  // Updated to handle the "free" method properly for jafes2025.
  const handlePurchaseMethod = useCallback(
    async (method, episodeId, ticketKey) => {
      setIsProcessing(true);
      try {
        if (method === "stripe") {
          await purchaseWithStripe();
        } else if (method === "paypay") {
          await purchaseWithPayPay();
        } else if (method === "free" && ticketKey === "1_day_free_jafes2025") {
          try {
            await claimFreeTicket(soulId, "jafes2025");
            // Optionally show success (e.g., navigate or set a state)
            setOpenDialog(false); // Close the dialog after success
          } catch (error) {
            console.error("Free claim error:", error);
            // Optionally set an error state (e.g., show a message)
          }
          setIsProcessing(false);
          return;
        }
      } catch (error) {
        console.error("Error:", error);
      }
      setIsProcessing(false);
    },
    [purchaseWithStripe, purchaseWithPayPay, soulId] // Dependencies
  );

  if (isLoading) {
    return (
      <AppLayout>
        <Loading />
      </AppLayout>
    );
  }

  if (isError) {
    return (
      <AppLayout>
        <Error />
      </AppLayout>
    );
  }

  if (emailRequired) {
    return (
      <AppLayout>
        <InputEmail soulId={soulId} onClickPurchase={navigateToPurchase} />
      </AppLayout>
    );
  }

  // ticketsのticket_keyに"repurchase"の文字列が含まれているものが一つでもあるかどうか
  const isRepurchase = tickets.some((ticket) =>
    ticket.ticket_key.includes("repurchase")
  );

  // Modify PurchaseDialog to accept ticketKey and add "Free" option
  const PurchaseDialog = ({
    open,
    onClose,
    onPurchaseMethod,
    isProcessing,
    lng,
    t,
    ticketKey,
  }) => {
    const stripeButton = (
      <StyledButton
        variant="contained"
        onClick={() => onPurchaseMethod("stripe")}
        sx={{
          width: "100%",
          fontSize: "16px",
          background: isProcessing ? "#ffffff" : "transparent",
        }}
        disabled={isProcessing}
      >
        {t("common:button.purchase_option_credit_card")}
      </StyledButton>
    );

    const payPayBox = (
      <Box sx={{ width: "100%" }}>
        {lng === "ja" && (
          <PayPayButton
            onClick={() => onPurchaseMethod("paypay")}
            disabled={isProcessing}
          />
        )}

        <Typography
          sx={{
            mt: 1,
            fontSize: "12px",
            color: COLORS.TEXT_BLACK,
            textAlign: "center",
            fontFamily: '"Noto Serif JP", serif',
            whiteSpace: "pre-line",
          }}
        >
          {/* {"※最後までPayPayアプリを\n閉じないでください"} */}
          {lng === "ja" && t("common:button.purchase_option_paypay")}
        </Typography>
      </Box>
    );

    const rawButtons =
      lng === "ja" ? [stripeButton, payPayBox] : [stripeButton];

    return (
      <ButtonDialog
        open={open}
        title={`${t("common:button.dialog.select_purchase")}`}
        rawButtons={rawButtons}
        handleClose={onClose}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `url(/${work}/background.png)`,
        backgroundSize: "100% auto",
        backgroundRepeat: "repeat",
        backgroundPosition: "top center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
          width: "100%",
          mb: 5,
        }}
      >
        <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
          {/* メイン画像 */}
          <img
            src={`/${work}/hero_image.png`}
            alt="hero image"
            width="100%"
            style={{ display: "block" }} // 隙間をなくす
          />
        </Box>

        {/* Story */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            width: "100%",
          }}
        >
          <Typography
            sx={(theme) => ({
              ...theme.components.sectionHeader,
            })}
          >
            {!isRepurchase && "Story"}
            {isRepurchase && "Epilogue"}
          </Typography>

          <Typography
            variant="h4"
            sx={(theme) => ({
              mt: 1,
              ...theme.components.storySection.name,
            })}
          >
            - {t(`${work}:character.${character}`)} -
          </Typography>

          {!isRepurchase && (
            <Typography
              sx={(theme) => ({
                mt: 2,
                textAlign: "center",
                letterSpacing: "0.1em",
                ...theme.components.storySection.story,
              })}
            >
              {t(`${work}:story.${character}`, {
                returnObjects: true,
              }).map((line, index) => {
                return (
                  <>
                    {line}
                    {index !==
                      t(`${work}:story.${character}`, {
                        returnObjects: true,
                      }).length -
                        1 && <br />}
                  </>
                );
              })}
            </Typography>
          )}

          {isRepurchase && (
            <Typography
              sx={(theme) => ({
                mt: 2,
                textAlign: "center",
                letterSpacing: "0.1em",
                ...theme.components.storySection.story,
              })}
            >
              {t(`${work}:epilogue.${character}`, {
                returnObjects: true,
              }).map((line, index) => {
                return (
                  <>
                    {line}
                    {index !==
                      t(`${work}:epilogue.${character}`, {
                        returnObjects: true,
                      }).length -
                        1 && <br />}
                  </>
                );
              })}
            </Typography>
          )}
        </Box>

        <Typography
          sx={(theme) => ({
            mt: 8,
            ...theme.components.sectionHeader,
          })}
        >
          Ticket
        </Typography>
        {tickets.length === 0 && (
          <Typography
            sx={(theme) => ({
              whiteSpace: "pre-line",
              lineHeight: 1.8,
              fontFamily: theme.system.fonts.body,
              color: theme.system.colors.text.primary,
              fontSize: theme.system.fontSizes.medium,
            })}
          >
            {t("common:error.ticket_not_available")}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            width: "100%",
          }}
        >
          {tickets.map((ticket) => (
            <Box key={ticket.ticket_key} sx={{ width: "100%" }}>
              <TicketBox
                ticket={ticket}
                onClick={() =>
                  onClickPurchase(ticket.episode_id, ticket.ticket_key)
                }
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Others Section */}
      <Box
        sx={{
          width: "100%",
          py: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          mt: 6,
        }}
      >
        <Typography
          sx={(theme) => ({
            letterSpacing: "0.06em",
            mb: 3,
            ...theme.components.sectionHeader,
          })}
        >
          Others
        </Typography>
        {listOtherAccountLinks(character).map((char, idx, arr) => (
          <Typography
            key={char.name}
            component="a"
            href={char.url}
            sx={{
              color: COLORS.TEXT_RED,
              fontFamily: '"Noto Serif JP", serif',
              fontSize: "24px",
              fontWeight: "bold",
              textDecoration: "none",
              mb: idx === arr.length - 1 ? 0 : 2,
              cursor: "pointer",
              transition: "opacity 0.2s",
              "&:hover": { opacity: 0.7 },
            }}
          >
            {t(`${work}:character.${char.name}`)}
          </Typography>
        ))}
      </Box>

      <Footer soulId={soulId} />

      {/* Remove the duplicate button at bottom or also add condition */}
      {tickets.length === 0 && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            py: 3,
            mt: 2,
          }}
        >
          <StyledButton
            onClick={() => setOpenFreeTrial(true)}
            variant="outlined"
            sx={{
              color: COLORS.TEXT_WHITE,
              borderColor: COLORS.TEXT_WHITE,
              fontSize: "16px",
              px: 4,
              py: 1.5,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            TRIAL
          </StyledButton>
        </Box>
      )}

      <ButtonDialog
        open={openCheckPayment}
        title={"PayPay購入による処理が正常に完了していない可能性があります。"}
        handleClose={handleCheckPaymentClose}
        buttons={[
          {
            label: "購入状況を確認",
            onClick: () => {
              navigate(
                `/${work}/check-paypay?soul_id=${soulId}&merchant_payment_id=${merchantPaymentId}&lng=${lng}` // merchantPaymentIdをクエリパラメータとして渡す
              );
            },
          },
        ]}
      />

      {/* Add new dialog for jafes code input */}
      <Dialog
        PaperProps={{
          style: {
            width: "80%",
          },
        }}
        open={openFreeJafes}
        onClose={() => setOpenFreeJafes(false)}
      >
        <Box
          sx={{
            minHeight: 200,
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 2,
              fontFamily: `Kaisei Decol, serif`,
              fontSize: "32px",
              color: COLORS.TEXT_RED,
            }}
          >
            コードを入力
          </Typography>
          <TextField
            label="Enter code"
            variant="standard"
            value={freeJafesCode}
            onChange={(e) => setFreeJafesCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleFreeJafesSubmit();
            }}
            error={false}
            inputProps={{
              spellCheck: false,
              autoComplete: "off",
              autoCorrect: "off",
              autoCapitalize: "off",
            }}
            sx={{
              mb: 1,
              width: "70%",
              "& .MuiInput-underline:before": {
                borderBottomColor: COLORS.TEXT_RED,
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: COLORS.TEXT_RED,
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: COLORS.TEXT_RED,
              },
              "& .MuiInputLabel-root": {
                fontFamily: '"Kiwi Maru", serif',
                color: COLORS.TEXT_BLACK,
                textAlign: "center",
                width: "100%",
                transformOrigin: "center center",
                "&.Mui-focused": {
                  color: COLORS.TEXT_BLACK,
                  fontFamily: '"Kiwi Maru", serif',
                },
                // Make label disappear when focused or has value
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  opacity: 0,
                },
              },
              "& .MuiInputBase-input": {
                color: COLORS.TEXT_BLACK,
                fontFamily: '"Kiwi Maru", serif',
                textAlign: "center",
              },
            }}
          />
          <Typography
            sx={{
              mt: 3,
              mb: 3,
              fontFamily: '"Kiwi Maru", serif',
              fontSize: "16px",
              color: COLORS.TEXT_RED,
              lineHeight: "2.0",
              whiteSpace: "pre-line",
              textAlign: "center",
            }}
          >
            {"邪フェス会場で配布される\nコードを入力してください"}
          </Typography>
          <StyledButton
            onClick={handleFreeJafesSubmit}
            variant="contained"
            disabled={isSubmittingCode}
            sx={{
              color: COLORS.TEXT_RED,
              mb: 1,
              width: "50%",
              fontFamily: '"Noto Serif JP", bold',
            }}
          >
            確定
          </StyledButton>
        </Box>
      </Dialog>

      {/* Add ButtonDialog for code error */}
      <ButtonDialog
        open={openCodeError}
        title={"コードが一致しません"}
        handleClose={() => setOpenCodeError(false)}
        buttons={[
          {
            label: "閉じる",
            onClick: () => setOpenCodeError(false),
          },
        ]}
      />

      {/* Modify PurchaseDialog call to pass ticketKey */}
      <PurchaseDialog
        open={openDialog} // Use existing state
        onClose={() => setOpenDialog(false)} // Use existing setter
        onPurchaseMethod={onClickPurchaseMethod} // Use the moved handler
        isProcessing={isProcessing}
        lng={lng}
        t={t}
        ticketKey={selectedTicketKey} // Use existing state
      />
    </Box>
  );
}

export default Tickets;
